import {
  UserIcon,
  UsersThreeIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import { PropsWithClassName } from '../../types/common';
import { Stack } from '../Stack';
import { ToggleGroup } from '../ToggleGroup';

export type PlanType = 'one_number' | 'family_plan';

interface PlanTypeToggleProps {
  value: PlanType;
  onChange: (value: PlanType) => void;
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  oneNumberLabel?: string;
  familyPlanLabel?: string;
  oneNumberError?: boolean;
  familyPlanError?: boolean;
}

// Plan Type selector shared by Portal forms and the websheet Watch templates:
// Watch (one_number) vs Watch Family Plan (family_plan).
// Labels are overridable so templates can pass localised copy.
// The error flags surface validation problems on either item, selected or
// not — a persistent signal at the toggle level even while the selected
// side also shows its errors inline.
export const PlanTypeToggle = ({
  value,
  onChange,
  className,
  isLoading = false,
  disabled = false,
  fullWidth = false,
  oneNumberLabel = 'Watch',
  familyPlanLabel = 'Watch Family Plan',
  oneNumberError = false,
  familyPlanError = false,
}: PropsWithClassName<PlanTypeToggleProps>) => {
  return (
    <ToggleGroup
      labelSize="md"
      label="Plan Type"
      isLoading={isLoading}
      fullWidth={fullWidth}
      className={className}
    >
      <ToggleGroup.Item
        value="one_number"
        checked={value === 'one_number'}
        onChange={() => {
          onChange('one_number');
        }}
        disabled={disabled}
      >
        <Stack direction="row" align="center" gap={2.5}>
          <UserIcon size={16} />
          {oneNumberLabel}
          {oneNumberError && (
            <WarningCircleIcon
              size={20}
              weight="fill"
              role="alert"
              aria-label="Error Detected Please Review Before Continuing"
              className="text-(--error)"
            />
          )}
        </Stack>
      </ToggleGroup.Item>
      <ToggleGroup.Item
        value="family_plan"
        checked={value === 'family_plan'}
        onChange={() => {
          onChange('family_plan');
        }}
        disabled={disabled}
      >
        <Stack direction="row" align="center" gap={2.5}>
          <UsersThreeIcon size={16} />
          {familyPlanLabel}
          {familyPlanError && (
            <WarningCircleIcon
              size={20}
              weight="fill"
              role="alert"
              aria-label="Error Detected Please Review Before Continuing"
              className="text-(--error)"
            />
          )}
        </Stack>
      </ToggleGroup.Item>
    </ToggleGroup>
  );
};
